import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../utils/api';

// Monthly VAT Summary composer (opened from "Email to Client" on the VAT credit bar). The draft is
// built server-side from the month's recorded figures — TPL-041 when VAT is payable, TPL-042 when a
// credit carries forward — and NOTHING is sent until the operator clicks Send: recipient, Cc,
// subject and body are all editable here first (owner's rule, 2026-08-19). Mirrors the
// PropertyTaxEmailComposer pattern.

const validEmail = (e: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);

function EmailChips({ value, onChange, ph }: { value: string[]; onChange: (v: string[]) => void; ph: string }) {
  const [q, setQ] = useState('');
  // Accept pasted lists ("a@b.com, c@d.com" / space-separated): every valid address becomes a chip;
  // anything invalid STAYS in the input so nothing is silently dropped.
  const add = (raw: string) => {
    const parts = raw.split(/[,;\s]+/).map(s => s.trim().toLowerCase()).filter(Boolean);
    const good = parts.filter(p => validEmail(p) && !value.includes(p));
    if (good.length) onChange([...value, ...Array.from(new Set(good))]);
    setQ(parts.filter(p => !validEmail(p)).join(' '));
  };
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center', padding: '6px 8px', border: '1px solid var(--border-strong)', borderRadius: 9, background: 'var(--c-gray-50)' }}>
      {value.map(e => (
        <span key={e} style={{ fontSize: 12, fontWeight: 600, padding: '3px 8px', borderRadius: 8, background: 'var(--accent-bg)', color: 'var(--accent-text)', display: 'inline-flex', alignItems: 'center', gap: 5 }}>
          {e}<span onClick={() => onChange(value.filter(x => x !== e))} style={{ cursor: 'pointer', fontWeight: 800 }}>×</span>
        </span>
      ))}
      <input value={q} onChange={e => setQ(e.target.value)} placeholder={ph}
        onKeyDown={e => { if ((e.key === 'Enter' || e.key === ',') && q.trim()) { e.preventDefault(); add(q); } }}
        onBlur={() => q.trim() && add(q)}
        style={{ flex: 1, minWidth: 140, border: 'none', outline: 'none', background: 'transparent', fontSize: 13, color: 'var(--text-primary)' }} />
    </div>
  );
}

export default function VatSummaryEmailComposer({ clientId, year, month, monthName, onClose, onSent }: {
  clientId: string; year: number; month: number; monthName: string; onClose: () => void; onSent?: () => void;
}) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState<string[]>([]);
  const [cc, setCc] = useState<string[]>([]);
  const [subject, setSubject] = useState('');
  const [initialBody, setInitialBody] = useState('');
  const [company, setCompany] = useState('');
  const [variant, setVariant] = useState<'payable' | 'credit' | ''>('');
  const [warning, setWarning] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [done, setDone] = useState(false);
  const [prepFailed, setPrepFailed] = useState(false);
  const bodyRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    let alive = true;
    api.prepareVatSummary(clientId, year, month).then((d: any) => {
      if (!alive) return;
      setFrom(d.from || ''); setTo(d.to || []); setCc(d.cc || []);
      setSubject(d.subject || ''); setInitialBody(d.bodyHtml || '');
      setCompany(d.figures?.clientName || ''); setVariant(d.variant || '');
      setWarning(d.warning || '');
    }).catch((e: any) => { if (alive) { setErr(e?.message || 'Failed to prepare email'); setPrepFailed(true); } }).finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [clientId, year, month]);

  const send = async () => {
    const body = bodyRef.current ? bodyRef.current.innerHTML : initialBody;
    if (!to.length) { setErr(t('iv_email_need')); return; }
    setBusy(true); setErr('');
    try { await api.sendVatSummary(clientId, { year, month, from, to, cc, subject, bodyHtml: body }); setDone(true); onSent?.(); setTimeout(onClose, 1200); }
    catch (e: any) { setErr(e?.message || 'Failed to send'); } finally { setBusy(false); }
  };

  const lbl: React.CSSProperties = { fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.03em', color: 'var(--text-secondary)', display: 'block', marginBottom: 5 };
  const inp: React.CSSProperties = { width: '100%', padding: '9px 11px', fontSize: 13, border: '1px solid var(--border-strong)', borderRadius: 9, background: 'var(--c-gray-50)', color: 'var(--text-primary)', outline: 'none', boxSizing: 'border-box' };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 10003, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onMouseDown={e => !busy && e.target === e.currentTarget && onClose()}>
      <div style={{ width: 640, maxWidth: '94vw', maxHeight: '92vh', overflowY: 'auto', background: 'var(--bg-surface)', borderRadius: 14, padding: 22, boxShadow: 'var(--shadow-md)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
          <div style={{ fontSize: 16, fontWeight: 800, color: 'var(--text-primary)' }}>{t('tx_vatmail_title')}</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {variant && (
              <span style={{ fontSize: 11.5, fontWeight: 800, padding: '4px 10px', borderRadius: 999, background: variant === 'payable' ? '#FEF3C7' : '#D1FAE5', color: variant === 'payable' ? '#92400E' : '#065F46' }}>
                {variant === 'payable' ? t('tx_credit_payable') : t('tx_credit_cf')}
              </span>
            )}
            <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--accent)', background: 'var(--accent-bg)', padding: '4px 11px', borderRadius: 999 }}>{monthName} {year}{company ? ` · ${company}` : ''}</span>
          </div>
        </div>
        <div style={{ fontSize: 12, color: 'var(--text-tertiary)', marginTop: 3 }}>{t('tx_vatmail_hint')}</div>

        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}><i className="ti ti-loader" style={{ fontSize: 24, color: 'var(--text-tertiary)' }} /></div>
        ) : prepFailed ? (
          // The draft could not be built (no chain / server error) — never offer an empty, sendable form.
          <div style={{ marginTop: 14 }}>
            <div style={{ fontSize: 13, color: '#B42318', background: '#FDE8E8', border: '1px solid #F5B5B1', borderRadius: 9, padding: '10px 13px' }}>
              <i className="ti ti-alert-octagon" style={{ marginRight: 6 }} />{err}
            </div>
            <div style={{ display: 'flex', marginTop: 18, justifyContent: 'flex-end' }}>
              <button onClick={onClose} style={{ padding: '9px 18px', borderRadius: 10, border: 'none', background: 'var(--c-gray-100)', color: 'var(--text-secondary)', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>{t('cancel')}</button>
            </div>
          </div>
        ) : (
          <div style={{ marginTop: 14 }}>
            {warning && <div style={{ fontSize: 12.5, color: '#92400E', background: '#FEF3C7', borderRadius: 9, padding: '8px 11px', marginBottom: 12 }}><i className="ti ti-alert-triangle" style={{ marginRight: 6 }} />{warning}</div>}

            <label style={lbl}>{t('bie_from')}</label>
            <input value={from} onChange={e => setFrom(e.target.value)} style={inp} />

            <label style={{ ...lbl, marginTop: 13 }}>{t('bie_to')}</label>
            <EmailChips value={to} onChange={setTo} ph={t('bie_email_ph')} />

            <label style={{ ...lbl, marginTop: 13 }}>{t('bie_cc')}</label>
            <EmailChips value={cc} onChange={setCc} ph={t('bie_email_ph')} />

            <label style={{ ...lbl, marginTop: 13 }}>{t('bie_subject')}</label>
            <input value={subject} onChange={e => setSubject(e.target.value)} style={inp} />

            <label style={{ ...lbl, marginTop: 13 }}>{t('bie_body')}</label>
            <div ref={bodyRef} contentEditable suppressContentEditableWarning
              dangerouslySetInnerHTML={{ __html: initialBody }}
              style={{ minHeight: 220, maxHeight: 380, overflowY: 'auto', padding: '11px 13px', fontSize: 13.5, lineHeight: 1.6, border: '1px solid var(--border-strong)', borderRadius: 9, background: 'var(--bg-surface)', color: 'var(--text-primary)', outline: 'none' }} />

            {err && <div style={{ fontSize: 12.5, color: '#E24B4A', marginTop: 12 }}>{err}</div>}
            {done ? (
              <div style={{ fontSize: 13.5, color: '#15803D', fontWeight: 700, marginTop: 16 }}><i className="ti ti-circle-check" style={{ marginRight: 6 }} />{t('iv_email_sent')}</div>
            ) : (
              <div style={{ display: 'flex', gap: 10, marginTop: 20, justifyContent: 'flex-end' }}>
                <button onClick={onClose} disabled={busy} style={{ padding: '9px 18px', borderRadius: 10, border: 'none', background: 'var(--c-gray-100)', color: 'var(--text-secondary)', fontWeight: 700, fontSize: 13, cursor: busy ? 'not-allowed' : 'pointer', opacity: busy ? 0.55 : 1 }}>{t('cancel')}</button>
                <button onClick={send} disabled={busy} style={{ padding: '9px 22px', borderRadius: 10, border: 'none', background: 'var(--accent)', color: '#fff', fontWeight: 800, fontSize: 13.5, cursor: busy ? 'not-allowed' : 'pointer', opacity: busy ? 0.55 : 1 }}>
                  {busy ? <><i className="ti ti-loader-2" style={{ display: 'inline-block', animation: 'spin 0.7s linear infinite', marginRight: 6 }} />{t('login_wait')}</> : t('iv_email_send')}</button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
